import { useState } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberVoucherDto, MemberVoucherStatus, VoucherTypeDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { errorMessage } from './errorMessage';
import {
  BUTTON,
  Field,
  FormError,
  INPUT,
  PANEL,
  SMALL_BUTTON,
  TournamentSelect,
  formatDay,
  useOperationKey,
  useWalletPermissions,
} from './walletShared';

type Action = { kind: 'redeem' | 'void' | 'replace'; voucher: MemberVoucherDto };

/** Reload the coupons so staff see whether an uncertain attempt went through. */
const reloadVouchers = (qc: QueryClient, memberId: string) =>
  qc.refetchQueries({ queryKey: ['member-vouchers', memberId] }, { throwOnError: true });

const STATUS_CLASS: Record<MemberVoucherStatus, string> = {
  UNUSED: 'bg-success/15 text-success',
  USED: 'bg-base-300 text-base-content/60',
  EXPIRED: 'bg-warning/15 text-warning',
  VOID: 'bg-base-300 text-base-content/60',
};

/** A coupon's last valid day: it stops working at the end of that Taipei day. */
const lastDay = (expiresAt: string | null) =>
  expiresAt ? formatDay(new Date(new Date(expiresAt).getTime() - 1).toISOString()) : null;

/** 票券紀錄: coupons are valid at every venue (S1). */
export function VouchersTab({ memberId, locked }: { memberId: string; locked: boolean }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const { canAdjust, canManage } = useWalletPermissions();
  const [status, setStatus] = useState<MemberVoucherStatus | ''>('');
  const [search, setSearch] = useState('');
  const [action, setAction] = useState<Action | null>(null);

  const { data: vouchers, isError } = useQuery<MemberVoucherDto[]>({
    queryKey: ['member-vouchers', memberId],
    queryFn: () => api.get(`/members/${memberId}/vouchers`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['member-vouchers', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
  };
  const term = search.trim().toLowerCase();
  const rows = (vouchers ?? []).filter(
    (v) =>
      (!status || v.status === status) &&
      (!term ||
        v.code.toLowerCase().includes(term) ||
        (v.originalCode ?? '').toLowerCase().includes(term) ||
        v.typeName.toLowerCase().includes(term)),
  );

  return (
    <div className="space-y-4">
      {canAdjust && !locked && <IssueVoucherForm memberId={memberId} onDone={refresh} />}
      {canManage && !locked && <ReenterVoucherForm memberId={memberId} onDone={refresh} />}

      <div className="grid gap-2 sm:grid-cols-2">
        <select
          aria-label={t('walletStatus')}
          className={INPUT}
          value={status}
          onChange={(e) => setStatus(e.target.value as MemberVoucherStatus | '')}
        >
          <option value="">{t('walletAllStatuses')}</option>
          {(['UNUSED', 'USED', 'EXPIRED', 'VOID'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`walletVoucherStatus_${s}`)}
            </option>
          ))}
        </select>
        <input
          type="search"
          aria-label={t('walletSearch')}
          placeholder={t('walletSearch')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={INPUT}
        />
      </div>

      {action && !locked && (
        <VoucherActionForm
          memberId={memberId}
          action={action}
          vouchers={vouchers ?? []}
          onCancel={() => setAction(null)}
          onDone={() => {
            setAction(null);
            refresh();
          }}
        />
      )}

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-base-300">
            <tr>
              {[
                'walletVoucherCode',
                'walletOriginalCode',
                'walletType',
                'walletPurpose',
                'walletAmount',
                'walletStatus',
                'walletIssuedVenue',
                'walletIssuedOn',
                'walletUsedOn',
                'walletExpiresOn',
                'walletUsedVenue',
                'ledgerNote',
                'walletActions',
              ].map((key) => (
                <th key={key} className="text-left px-3 py-2 whitespace-nowrap">
                  {t(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {rows.map((v) => (
              <tr key={v.id}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{v.code}</td>
                <td className="px-3 py-2 font-mono">{v.originalCode ?? '-'}</td>
                <td className="px-3 py-2">{v.typeName}</td>
                <td className="px-3 py-2">{t(`walletPurpose_${v.purpose}`)}</td>
                <td className="px-3 py-2 font-mono">
                  {v.amount ? Number(v.amount).toLocaleString() : '-'}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLASS[v.status]}`}>
                    {t(`walletVoucherStatus_${v.status}`)}
                  </span>
                  {v.replacedByVoucherId && (
                    <span className="block text-xs text-base-content/50">
                      {t('walletReplaced')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{v.issuedVenueName ?? '-'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDay(v.issuedAt)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {v.usedAt ? formatDay(v.usedAt) : '-'}
                  {v.usedEntryNo !== null && (
                    <span className="block text-xs text-base-content/50">
                      {t('walletEntryNoValue', { n: v.usedEntryNo })}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {lastDay(v.expiresAt) ?? t('walletNoExpiry')}
                </td>
                <td className="px-3 py-2">{v.usedVenueName ?? '-'}</td>
                <td className="px-3 py-2 [overflow-wrap:anywhere]">{v.note}</td>
                <td className="px-3 py-2">
                  {!locked && (
                    <div className="flex gap-1">
                      {canAdjust && v.status === 'UNUSED' && (
                        <button
                          type="button"
                          className={SMALL_BUTTON}
                          onClick={() => setAction({ kind: 'redeem', voucher: v })}
                        >
                          {t('walletRedeem')}
                        </button>
                      )}
                      {canManage && (v.status === 'UNUSED' || v.status === 'EXPIRED') && (
                        <button
                          type="button"
                          className={SMALL_BUTTON}
                          onClick={() => setAction({ kind: 'void', voucher: v })}
                        >
                          {t('walletVoid')}
                        </button>
                      )}
                      {canManage && v.status === 'USED' && !v.replacedByVoucherId && (
                        <button
                          type="button"
                          className={SMALL_BUTTON}
                          onClick={() => setAction({ kind: 'replace', voucher: v })}
                        >
                          {t('walletReplace')}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {vouchers && rows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={13} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function useVoucherTypes() {
  return useQuery<VoucherTypeDto[]>({
    queryKey: ['voucher-types'],
    queryFn: () => api.get('/members/voucher-types'),
  });
}

function IssueVoucherForm({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const { t } = useTranslation('member');
  const { data: types } = useVoucherTypes();
  const [typeId, setTypeId] = useState('');
  const [message, setMessage] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey({ typeId, message, note }, () => reloadVouchers(qc, memberId));
  const issue = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/vouchers`, {
        typeId,
        message: message.trim(),
        note: note.trim(),
        operationKey: op.key,
      }),
    onSuccess: () => {
      setError('');
      setNote('');
      setMessage('');
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        issue.mutate();
      }}
    >
      <h3 className="font-medium">{t('walletIssueVoucher')}</h3>
      <fieldset disabled={issue.isPending} className="grid gap-3 sm:grid-cols-3">
        <Field label={t('walletType')}>
          <select className={INPUT} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t('walletPickType')}</option>
            {(types ?? [])
              .filter((x) => x.isActive)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label={t('walletMessage')}>
          <input
            className={INPUT}
            maxLength={255}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
        <Field label={t('ledgerNote')}>
          <input
            className={INPUT}
            maxLength={255}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </fieldset>
      <FormError message={error} />
      <button
        type="submit"
        className={BUTTON}
        disabled={!typeId || !note.trim() || issue.isPending || !op.ready}
      >
        {error ? t('walletRetry') : t('walletIssueVoucher')}
      </button>
    </form>
  );
}

function ReenterVoucherForm({ memberId, onDone }: { memberId: string; onDone: () => void }) {
  const { t } = useTranslation('member');
  const { data: types } = useVoucherTypes();
  const [show, setShow] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey({ typeId, expiresOn, originalCode, note }, () =>
    reloadVouchers(qc, memberId),
  );
  const save = useMutation({
    mutationFn: () =>
      api.post(`/members/${memberId}/vouchers/reentry`, {
        typeId,
        expiresOn: expiresOn || null,
        originalCode: originalCode.trim(),
        note: note.trim(),
        operationKey: op.key,
      }),
    onSuccess: () => {
      setError('');
      setOriginalCode('');
      setNote('');
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  if (!show)
    return (
      <button type="button" className={SMALL_BUTTON} onClick={() => setShow(true)}>
        {t('walletReenter')}
      </button>
    );
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3 className="font-medium">{t('walletReenterVoucher')}</h3>
      <fieldset disabled={save.isPending} className="grid gap-3 sm:grid-cols-2">
        <Field label={t('walletType')}>
          <select className={INPUT} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">{t('walletPickType')}</option>
            {(types ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('walletExpiresOn')}>
          <input
            className={INPUT}
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
        </Field>
        <Field label={t('walletOriginalCode')}>
          <input
            className={INPUT}
            maxLength={40}
            value={originalCode}
            onChange={(e) => setOriginalCode(e.target.value)}
          />
        </Field>
        <Field label={t('ledgerNote')}>
          <input
            className={INPUT}
            maxLength={255}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </fieldset>
      <FormError message={error} />
      <div className="flex gap-2">
        <button
          type="submit"
          className={BUTTON}
          disabled={!typeId || !originalCode.trim() || !note.trim() || save.isPending || !op.ready}
        >
          {error ? t('walletRetry') : t('ledgerSave')}
        </button>
        <button type="button" className={SMALL_BUTTON} onClick={() => setShow(false)}>
          {t('walletCancel')}
        </button>
      </div>
    </form>
  );
}

function VoucherActionForm({
  memberId,
  action,
  vouchers,
  onCancel,
  onDone,
}: {
  memberId: string;
  action: Action;
  vouchers: MemberVoucherDto[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('member');
  const [tournamentId, setTournamentId] = useState('');
  const [entryNo, setEntryNo] = useState('1');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const op = useOperationKey({ kind: action.kind, id: action.voucher.id, text }, () =>
    reloadVouchers(qc, memberId),
  );
  const base = `/members/${memberId}/vouchers/${action.voucher.id}`;
  const submit = useMutation({
    mutationFn: () => {
      if (action.kind === 'redeem')
        return api.post(`${base}/redeem`, {
          tournamentId,
          entryNo: Number(entryNo),
          note: text.trim(),
        });
      if (action.kind === 'replace')
        return api.post(`${base}/replace`, { reason: text.trim(), operationKey: op.key });
      return api.post(`${base}/void`, { reason: text.trim() });
    },
    onSuccess: () => {
      op.renew();
      onDone();
    },
    onError: (failure) => {
      op.failed(failure);
      setError(errorMessage(failure, t('ledgerLoadFailed')));
    },
  });
  // Coupons already used on the chosen entry (a replaced mistake no longer counts).
  const onEntry = vouchers.filter(
    (v) =>
      v.status === 'USED' &&
      !v.replacedByVoucherId &&
      v.usedTournamentId === tournamentId &&
      v.usedEntryNo === Number(entryNo),
  );
  const valid =
    !!text.trim() && (action.kind !== 'redeem' || (!!tournamentId && Number(entryNo) >= 1));
  const title = {
    redeem: t('walletRedeem'),
    void: t('walletVoid'),
    replace: t('walletReplace'),
  }[action.kind];
  return (
    <form
      className={PANEL}
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <h3 className="font-medium">
        {title} · <span className="font-mono">{action.voucher.code}</span> ·{' '}
        {action.voucher.typeName}
      </h3>
      <fieldset disabled={submit.isPending} className="grid gap-3 sm:grid-cols-3">
        {action.kind === 'redeem' && (
          <>
            <Field label={t('walletTournament')}>
              <TournamentSelect value={tournamentId} onChange={setTournamentId} />
            </Field>
            <Field label={t('walletEntryNo')}>
              <input
                className={INPUT}
                type="number"
                min="1"
                inputMode="numeric"
                value={entryNo}
                onChange={(e) => setEntryNo(e.target.value)}
              />
            </Field>
          </>
        )}
        <Field label={action.kind === 'redeem' ? t('ledgerNote') : t('walletReason')}>
          <input
            className={INPUT}
            maxLength={255}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
      </fieldset>
      {action.kind === 'redeem' && (
        <>
          <p className="text-xs text-base-content/60">{t('walletEntryNoHint')}</p>
          <p className="text-xs text-base-content/60">{t('walletRedeemManual')}</p>
          {tournamentId && (
            <div className="text-sm">
              <p className="text-base-content/70">{t('walletUsedOnEntry')}</p>
              {onEntry.length === 0 ? (
                <p className="text-base-content/50">{t('walletNone')}</p>
              ) : (
                <ul className="list-disc pl-5">
                  {onEntry.map((v) => (
                    <li key={v.id}>
                      <span className="font-mono">{v.code}</span> · {v.typeName}
                      {v.stacksWithOtherCoupons && ` · ${t('walletStacks')}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      <FormError message={error} />
      <div className="flex gap-2">
        <button type="submit" className={BUTTON} disabled={!valid || submit.isPending || !op.ready}>
          {error ? t('walletRetry') : title}
        </button>
        <button type="button" className={SMALL_BUTTON} onClick={onCancel}>
          {t('walletCancel')}
        </button>
      </div>
    </form>
  );
}
